import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { useIntl } from 'react-intl';
import { ChevronDownIcon } from '@/components/layout/icons';
import { useListParams } from '@/lib/list-view/use-list-params';

export type SortDirection = 'asc' | 'desc';
export type SortState<TField extends string> = { readonly field: TField; readonly direction: SortDirection } | null;
export type SortValue = string | number | null;

export type SortStateHandle<TField extends string> = readonly [
  SortState<TField>,
  Dispatch<SetStateAction<SortState<TField>>>,
];

export const ARIA_SORT = { asc: 'ascending', desc: 'descending' } as const;

export function sortRows<TField extends string, TRow>(
  rows: ReadonlyArray<TRow>,
  sort: SortState<TField>,
  sortKey: (row: TRow, field: TField) => SortValue,
  locale: string,
): ReadonlyArray<TRow> {
  if (sort === null) return rows;

  const collator = new Intl.Collator(locale);
  const factor = sort.direction === 'asc' ? 1 : -1;

  // One key computation per row, not two per comparison: a key can be a translated, joined label.
  const keyed = rows.map((row) => ({ row, key: sortKey(row, sort.field) }));

  keyed.sort((a, b) => {
    const left = a.key;
    const right = b.key;

    if (left === null && right === null) return 0;

    if (left === null) return 1;

    if (right === null) return -1;

    if (typeof left === 'number' && typeof right === 'number') return (left - right) * factor;

    return collator.compare(String(left), String(right)) * factor;
  });

  return keyed.map(({ row }) => row);
}

const nextSort = <TField extends string>(current: SortState<TField>, field: TField): SortState<TField> => {
  if (current === null || current.field !== field) return { field, direction: 'asc' };

  if (current.direction === 'asc') return { field, direction: 'desc' };

  return null;
};

/**
 * Client-side sort over an already-fetched page. The third click returns the
 * server's array by identity: its order is not reproducible from any one column.
 * Empty values (`null`) group last in both directions.
 */
export function useTableSort<TField extends string, TRow>(
  rows: ReadonlyArray<TRow>,
  sortKey: (row: TRow, field: TField) => SortValue,
  // A table that unmounts while its sort must survive (the Fleet cards under the
  // loading skeleton) passes a state its parent holds.
  held?: SortStateHandle<TField>,
) {
  const { locale } = useIntl();
  const own = useState<SortState<TField>>(null);
  const [sort, setSortState] = held ?? own;

  const sorted = useMemo(() => sortRows(rows, sort, sortKey, locale), [rows, sort, sortKey, locale]);

  const toggleSort = useCallback(
    (field: TField) => setSortState((current) => nextSort(current, field)),
    [setSortState],
  );

  const setSort = useCallback(
    (field: TField, direction: SortDirection) => setSortState({ field, direction }),
    [setSortState],
  );

  return { sorted, sort, toggleSort, setSort };
}

/**
 * `useTableSort` with the sort kept in the address as `sort` and `dir` (optionally prefixed), through
 * the shared list params so one click is one history entry. `fields` and `sortKey` must be stable so
 * the memo holds. A `sort` outside `fields` reads as no sort, a `dir` other than desc as ascending.
 */
export function useUrlTableSort<TField extends string, TRow>(
  rows: ReadonlyArray<TRow>,
  sortKey: (row: TRow, field: TField) => SortValue,
  { fields, paramPrefix = '' }: { readonly fields: ReadonlyArray<TField>; readonly paramPrefix?: string },
) {
  const { locale } = useIntl();
  const { params, setParams } = useListParams();
  const sortParam = `${paramPrefix}sort`;
  const dirParam = `${paramPrefix}dir`;
  const rawField = params.get(sortParam);
  const field = fields.find((candidate) => candidate === rawField) ?? null;
  const direction: SortDirection | null = field === null ? null : params.get(dirParam) === 'desc' ? 'desc' : 'asc';

  const fromAddress = useMemo<SortState<TField>>(
    () => (field === null || direction === null ? null : { field, direction }),
    [field, direction],
  );

  // The router applies an address change a beat after the click. Until it lands, the clicked sort
  // shows at once. Clicks quicker than the router queue up: each address on the way to the last
  // click's (`chain`) keeps the pending sort, so an older navigation landing never flashes back an
  // earlier order. Reaching the target, or any address off that path (Back, a link), drops it, so
  // the address is the truth again.
  const addressKey = `${rawField ?? ''}|${params.get(dirParam) ?? ''}`;
  const [pending, setPending] = useState<{
    readonly chain: ReadonlyArray<string>;
    readonly target: string;
    readonly sort: SortState<TField>;
  } | null>(null);
  const inFlight = pending !== null && pending.target !== addressKey && pending.chain.includes(addressKey);

  // Clears only the pending sort this render saw: a click handled between this render and the
  // effect has already queued a newer one, and wiping that flashed the earlier order back.
  useEffect(() => {
    if (pending !== null && !inFlight) setPending((current) => (current === pending ? null : current));
  }, [pending, inFlight]);

  const sort = inFlight ? pending.sort : fromAddress;
  const sorted = useMemo(() => sortRows(rows, sort, sortKey, locale), [rows, sort, sortKey, locale]);

  const apply = useCallback(
    (next: SortState<TField>) => {
      setPending((current) => ({
        chain:
          current !== null && current.target !== addressKey && current.chain.includes(addressKey)
            ? [...current.chain, current.target]
            : [addressKey],
        target: next === null ? '|' : `${next.field}|${next.direction}`,
        sort: next,
      }));
      setParams(
        next === null
          ? { [sortParam]: null, [dirParam]: null }
          : { [sortParam]: next.field, [dirParam]: next.direction },
      );
    },
    [addressKey, setParams, sortParam, dirParam],
  );

  const setSort = useCallback(
    (nextField: TField, nextDirection: SortDirection) => apply({ field: nextField, direction: nextDirection }),
    [apply],
  );

  const toggleSort = useCallback((nextField: TField) => apply(nextSort(sort, nextField)), [apply, sort]);

  return { sorted, sort, toggleSort, setSort };
}

type SortableHeaderProps<TField extends string> = {
  readonly field: TField;
  readonly label: ReactNode;
  readonly ariaLabel: string;
  readonly sort: SortState<TField>;
  readonly onSort: (field: TField) => void;
  readonly align?: 'left' | 'right';
};

/** A column head that is a real control, so keyboard operability comes from the element itself. */
export function SortableHeader<TField extends string>({
  field,
  label,
  ariaLabel,
  sort,
  onSort,
  align = 'left',
}: SortableHeaderProps<TField>) {
  const active = sort !== null && sort.field === field;

  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      aria-label={ariaLabel}
      className={`group inline-flex w-full items-center gap-1 ${align === 'right' ? 'justify-end' : 'justify-start'}`}
    >
      {label}
      <ChevronDownIcon
        className={`h-3 w-3 shrink-0 transition ${active ? 'text-text-secondary' : 'text-text-placeholder opacity-0 group-hover:opacity-100'} ${active && sort.direction === 'asc' ? 'rotate-180' : ''}`}
        aria-hidden
      />
    </button>
  );
}
